"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Play, ExternalLink, CircleCheck as CheckCircle2, Sparkles, Layers, Activity } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
const highlights = [{
  id: 1,
  title: "Atomização de Alta Precisão",
  description: "Controlo volumétrico e distribuição uniforme de gotas.",
  icon: Activity
}, {
  id: 2,
  title: "Sistemas Spraying Systems Co.",
  description: "Tecnologia industrial comprovada globalmente desde 1937.",
  icon: Layers
}, {
  id: 3,
  title: "Otimização de Consumos",
  description: "Redução de desperdício de água, químicos e energia.",
  icon: CheckCircle2
}];
export default function VideoShowcase() {
  const [isOpen, setIsOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const videoId = "qJWfgWquagg";
  const videoUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
  const youtubeDirectUrl = `https://www.youtube.com/watch?v=${videoId}`;
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section id="videoshowcase" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="flex flex-col items-center text-center max-w-3xl mx-auto mb-14">
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-accent text-accent-foreground text-xs sm:text-sm font-medium mb-4 border border-border">
            <Sparkles className="size-4" />
            <span>Demonstração Técnica em Vídeo</span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-5">
            Engenharia de Pulverização em Ação
          </motion.h2>

          <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Descubra como as soluções da Eurocontrol e da Spraying Systems Co.
            transformam os processos de atomização, limpeza e revestimento
            industrial através de precisão milimétrica e tecnologia de ponta.
          </motion.p>
        </motion.div>

        {}
        <motion.div initial={{
        opacity: 0,
        scale: shouldReduceMotion ? 1 : 0.98
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6
      }} className="mx-auto max-w-5xl">
          <div className="group relative rounded-2xl border border-border bg-card shadow-2xl overflow-hidden p-2 sm:p-3 transition-[border-color,box-shadow] duration-300 hover:border-primary/50">
            <div className="relative overflow-hidden rounded-xl bg-muted">
              <AspectRatio ratio={16 / 9}>
                <img src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} alt="Demonstração de sistemas de pulverização industrial Spraying Systems Co." loading="lazy" decoding="async" className="size-full object-cover transition-transform duration-700 group-hover:scale-105" />

                {}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent opacity-80 group-hover:opacity-65 transition-opacity" />
                <div className="absolute inset-0 bg-foreground/15 mix-blend-multiply" />

                {}
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  <Button type="button" variant="ghost" size="icon" onClick={() => setIsOpen(true)} className="relative size-20 sm:size-24 rounded-lg bg-background/90 text-primary hover:bg-background hover:scale-110 shadow-2xl backdrop-blur-md transition-all duration-300 group-hover:ring-8 group-hover:ring-primary/20 focus-visible:ring-4 focus-visible:ring-ring" aria-label="Reproduzir vídeo de demonstração industrial">
                    <span className="absolute -inset-2 rounded-lg bg-primary/20 animate-ping opacity-40 group-hover:opacity-75 pointer-events-none" />
                    <Play className="size-8 sm:size-10 fill-primary translate-x-0.5" />
                  </Button>
                </div>

                {}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-background/90 text-foreground text-xs font-semibold backdrop-blur-sm border border-border/50 shadow-sm">
                    <span className="size-2 rounded-full bg-chart-3 animate-pulse" />
                    Tecnologia Spraying Systems Co.
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-primary/90 text-primary-foreground text-xs font-mono font-medium backdrop-blur-sm">
                    03:45 min
                  </span>
                </div>

                <div className="absolute bottom-4 left-4 right-4 flex flex-col sm:flex-row sm:items-end justify-between gap-3 pointer-events-none">
                  <div>
                    <p className="text-white text-base sm:text-xl font-bold drop-shadow-md">
                      Controlo de Pulverização de Precisão
                    </p>
                    <p className="text-white/80 text-xs sm:text-sm drop-shadow-sm">
                      Ensaios dinâmicos em túnel de vento e câmara de diagnóstico laser
                    </p>
                  </div>

                  <div className="pointer-events-auto">
                    <Button variant="secondary" size="sm" onClick={() => setIsOpen(true)} className="shadow-md bg-secondary text-secondary-foreground hover:bg-secondary/90 text-xs sm:text-sm">
                      <Play className="size-3.5 fill-current mr-1.5" />
                      Assistir Vídeo
                    </Button>
                  </div>
                </div>
              </AspectRatio>
            </div>
          </div>
        </motion.div>

        {}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {highlights.map((item, i) => {
          const Icon = item.icon;
          return <div key={item.id} data-index={i} className="flex items-start gap-4 p-5 rounded-xl bg-card border border-border shadow-sm hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <div className="size-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                  <Icon className="size-5" />
                </div>
                <div>
                  <h3 className="font-default text-base font-semibold text-foreground mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-12 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
            <Link to="/TechnicalSolutionsPage">
              Explorar Soluções Técnicas
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted">
            <Link to="/ContactsPage">
              Solicitar Demonstração à Medida
            </Link>
          </Button>
        </div>
      </div>

      {}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden bg-card border-border shadow-2xl" showCloseButton={true}>
          <div className="p-5 border-b border-border bg-background">
            <DialogHeader>
              <DialogTitle className="text-xl font-default font-bold text-foreground">
                Demonstração de Sistemas de Pulverização
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Tecnologias de atomização, bicos hidráulicos e controladores automatizados Eurocontrol.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-3 sm:p-5 bg-background">
            <div className="rounded-lg overflow-hidden bg-black border border-border shadow-inner">
              <AspectRatio ratio={16 / 9}>
                {isOpen && <iframe src={videoUrl} title="Vídeo de Demonstração Eurocontrol Spraying Systems" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="size-full border-0" />}
              </AspectRatio>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-muted-foreground pt-3 border-t border-border">
              <span>Problemas com a reprodução integrada?</span>
              <Button asChild variant="link" size="sm" className="h-auto p-0 text-secondary">
                <Link to={youtubeDirectUrl} newTab className="inline-flex items-center gap-1.5 font-medium hover:underline">
                  <span>Abrir diretamente no YouTube</span>
                  <ExternalLink className="size-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>;
}